import React from 'react'
import { cn } from '@amusnet/react-ui'

// Ultra Play
import speedButton0 from '@assets/speed-buttons/speed-button-0.svg?inline'
import speedButton1 from '@assets/speed-buttons/speed-button-1.svg?inline'
import speedButton2 from '@assets/speed-buttons/speed-button-2.svg?inline'
import speedButton3 from '@assets/speed-buttons/speed-button-3.svg?inline'

import speedButtonEQ0 from '@assets/speed-buttons/speed-button-0-eq.svg?inline'
import speedButtonEQ1 from '@assets/speed-buttons/speed-button-1-eq.svg?inline'
import speedButtonEQ2 from '@assets/speed-buttons/speed-button-2-eq.svg?inline'
import speedButtonEQ3 from '@assets/speed-buttons/speed-button-3-eq.svg?inline'

// No Ultra Play
import threeLevelSpeedButton0 from '@assets/speed-button-0.svg?inline'
import threeLevelSpeedButton1 from '@assets/speed-button-1.svg?inline'
import threeLevelSpeedButton2 from '@assets/speed-button-2.svg?inline'

import fourLevelSpeedButton0 from '@assets/speed-button-small-enabled-0.svg?inline'
import fourLevelSpeedButton1 from '@assets/speed-button-small-enabled-1.svg?inline'
import fourLevelSpeedButton2 from '@assets/speed-button-small-enabled-2.svg?inline'
import fourLevelSpeedButton3 from '@assets/speed-button-small-enabled-3.svg?inline'

import fourLevelSpeedButtonEQ0 from '@assets/jackpot-eq/speed-button-small-enabled-0.svg?inline'
import fourLevelSpeedButtonEQ1 from '@assets/jackpot-eq/speed-button-small-enabled-1.svg?inline'
import fourLevelSpeedButtonEQ2 from '@assets/jackpot-eq/speed-button-small-enabled-2.svg?inline'
import fourLevelSpeedButtonEQ3 from '@assets/jackpot-eq/speed-button-small-enabled-3.svg?inline'

import ultraPlayIcon from '@assets/speed-buttons/ultraplay.svg?inline'
import ultraPlayIconEQ from '@assets/speed-buttons/ultraplay-eq.svg?inline'
import speedButtonSeparator from '@assets/speed-buttons/speed-button-separator.svg?inline'
import speedButtonSeparatorEQ from '@assets/speed-buttons/separator-ultraplay-EQ.svg?inline'

import { withAutoBlur } from '../../../utils/misc'
import { GameUIMessageType, SpeedClickedMessage } from 'game-ui-types'
import { useConfigurationStore, useJackpotStore, useSoundStore, useUltraPlayStore } from '@stores'
import { useHasActiveBonus, useIsUltraPlayAvailable } from '@hooks'
import { SOUNDS } from '@constants/sounds'

interface SpeedButtonProps {
    isActive: boolean
    speedLevel: number
    isUltraPlayButtonActive?: boolean
}

const SPEED_BUTTON_TEXTURES = [speedButton0, speedButton1, speedButton2, speedButton3]
const SPEED_BUTTON_EQ_TEXTURES = [speedButtonEQ0, speedButtonEQ1, speedButtonEQ2, speedButtonEQ3]
const THREE_LEVEL_SPEED_BUTTON_TEXTURES = [threeLevelSpeedButton0, threeLevelSpeedButton1, threeLevelSpeedButton2]
const FOUR_LEVEL_SPEED_BUTTON_TEXTURES = [
    fourLevelSpeedButton0,
    fourLevelSpeedButton1,
    fourLevelSpeedButton2,
    fourLevelSpeedButton3,
]
const FOUR_LEVEL_SPEED_BUTTON_EQ_TEXTURES = [
    fourLevelSpeedButtonEQ0,
    fourLevelSpeedButtonEQ1,
    fourLevelSpeedButtonEQ2,
    fourLevelSpeedButtonEQ3,
]

const SpeedButton: React.FC<SpeedButtonProps> = ({ isActive, speedLevel, isUltraPlayButtonActive }) => {
    const isEQJackpotType = useJackpotStore((state) => state.isEQJackpotType)
    const isJackpotRoundUnfinished = useJackpotStore((state) => state.isJackpotRoundUnfinished)
    const shouldEnableUltraSpeed = useConfigurationStore((state) => state.shouldEnableUltraSpeed)
    const isReplayMode = useConfigurationStore((state) => state.isReplayMode)
    const isUltraPlayAvailable = useIsUltraPlayAvailable()

    const openUltraPlay = useUltraPlayStore((state) => state.open)
    const ultraPlayStatus = useUltraPlayStore((state) => state.status)
    const isUltraPlayPlaying = ultraPlayStatus === 'PLAYING'
    // During an active bonus the bet is locked, so Ultra Play (which can change the bet) is disabled.
    const hasActiveBonus = useHasActiveBonus()
    const playEngineSound = useSoundStore((state) => state.playEngineSound)

    const isUltraPlayBlocked = hasActiveBonus || isJackpotRoundUnfinished || isReplayMode
    const isSpeedButtonActive = isActive && !isReplayMode

    const handleOpenUltraPlay = () => {
        playEngineSound(SOUNDS.SHOW_DENOMINATIONS)
        openUltraPlay()
    }

    const handleSpeedClicked = withAutoBlur(() => {
        const msg: SpeedClickedMessage = { type: GameUIMessageType.SPEED_CLICKED }

        window.uiBus.postMessage(msg)
    })

    if (isUltraPlayAvailable) {
        const textures = isEQJackpotType ? SPEED_BUTTON_EQ_TEXTURES : SPEED_BUTTON_TEXTURES
        const texture = textures[Math.min(speedLevel, textures.length - 1)]
        const ultraPlayTexture = !isEQJackpotType ? ultraPlayIcon : ultraPlayIconEQ
        const separatorTexture = !isEQJackpotType ? speedButtonSeparator : speedButtonSeparatorEQ

        return (
            <div className="flex h-[58px]">
                <button
                    id="speed-button"
                    data-speed={speedLevel}
                    onClick={(e) => handleSpeedClicked(e)}
                    className={cn('focus:outline-none', !isSpeedButtonActive && 'opacity-50 pointer-events-none')}
                >
                    <img src={texture} alt="Speed Button" className="h-full" />
                </button>

                <img src={separatorTexture} alt="" className="w-[4px] h-full flex-shrink-0" aria-hidden="true" />

                <button
                    id="ultra-play-button"
                    onClick={isUltraPlayBlocked ? undefined : handleOpenUltraPlay}
                    className={cn(
                        'focus:outline-none relative',
                        (!isUltraPlayButtonActive || isUltraPlayBlocked) && 'opacity-50 pointer-events-none'
                    )}
                    aria-label="Open Ultra Play"
                >
                    <img src={ultraPlayTexture} alt="Ultra Play" className="h-full" />
                    {isUltraPlayPlaying && (
                        <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#4CAF50]" />
                    )}
                </button>
            </div>
        )
    }

    const textures = shouldEnableUltraSpeed
        ? isEQJackpotType
            ? FOUR_LEVEL_SPEED_BUTTON_EQ_TEXTURES
            : FOUR_LEVEL_SPEED_BUTTON_TEXTURES
        : THREE_LEVEL_SPEED_BUTTON_TEXTURES
    const texture = textures[Math.min(speedLevel, textures.length - 1)]

    return (
        <button
            id="speed-button"
            data-speed={speedLevel}
            className={cn(
                'w-[110px] h-[58px] focus:outline-none',
                !isSpeedButtonActive && 'opacity-50 pointer-events-none'
            )}
            onClick={(e) => handleSpeedClicked(e)}
        >
            <img src={texture} alt="Speed Button" className="w-full h-full" />
        </button>
    )
}

export default SpeedButton
